import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DateInput from "../components/DateInput";
import { useParams, useNavigate, Link } from "react-router-dom";
import ResponsiveGrid from "../components/ResponsiveGrid";
import ERPAnalyticsHeader from "../components/ERPAnalyticsHeader";
import WidgetRenderer from "../components/WidgetRenderer";
import { api, API_BASE } from "../lib/api";
import { formatDateValue, formatReportValue } from "../lib/dateFormat";
import { useAuth, canAtLeast } from "../lib/auth";
import { Download, Pencil, X } from "lucide-react";

export default function DashboardView() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [dash, setDash] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(new Date().toISOString());
  const [refreshing, setRefreshing] = useState(false);
  const [filterValues, setFilterValues] = useState({});  // { field: value }
  const [dropdownChoices, setDropdownChoices] = useState({}); // field -> [..]
  const [drill, setDrill] = useState(null);
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState("");
  const [drillSearch, setDrillSearch] = useState("");

  const fetchDashboard = useCallback(() => {
    setLoadError("");
    setDash(null);
    const ctrl = new AbortController();
    const timer = setTimeout(() => {
      ctrl.abort();
      setLoadError("Dashboard is taking longer than expected to load. Try again.");
    }, 15000);
    api.get(`/dashboards/${id}`, { signal: ctrl.signal, timeout: 15000 })
      .then((r) => setDash(r.data))
      .catch((e) => {
        if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
        setLoadError(e?.response?.data?.detail || e.message || "Failed to load dashboard");
      })
      .finally(() => clearTimeout(timer));
    return () => { clearTimeout(timer); try { ctrl.abort(); } catch (_) { /* noop */ } };
  }, [id]);

  useEffect(() => { return fetchDashboard(); }, [fetchDashboard]);

  // Load dropdown choices for any dropdown filter (best effort) — uses the FIRST widget with a dataset
  useEffect(() => {
    if (!dash) return;
    const widgetWithDs = (dash.widgets || []).find((w) => w.dataset_id);
    if (!widgetWithDs) return;
    (dash.filters || []).forEach((f) => {
      if (f.type === "dropdown") {
        api.get(`/filters/values?dataset_id=${encodeURIComponent(widgetWithDs.dataset_id)}&field=${encodeURIComponent(f.field)}`)
          .then((r) => setDropdownChoices((c) => ({ ...c, [f.field]: r.data.values || [] })))
          .catch(() => {});
      }
    });
  }, [dash]);

  // Compose dashboardFilters as list of clauses
  const dashboardFilters = useMemo(() => {
    const out = [];
    Object.entries(filterValues).forEach(([field, val]) => {
      if (val == null || val === "" || val === "all") return;
      if (typeof val === "object" && val.from === undefined && val.to === undefined && !Array.isArray(val)) return;
      if (typeof val === "object" && (val.from || val.to)) {
        out.push({ field, op: "date_range", value: val });
      } else {
        out.push({ field, op: "in", value: Array.isArray(val) ? val : [val] });
      }
    });
    return out;
  }, [filterValues]);

  const doRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshKey((k) => k + 1);
    setLastRefreshed(new Date().toISOString());
    setTimeout(() => setRefreshing(false), 600);
  }, []);

  const setFilter = (field, value) => setFilterValues((s) => ({ ...s, [field]: value }));
  const resetFilters = () => setFilterValues({});

  const orderedWidgets = useMemo(() => {
    const savedOrder = new Map(
      (dash?.layout || []).map((item, index) => [item.i, index])
    );

    return [...(dash?.widgets || [])].sort((a, b) => {
      return (savedOrder.get(a.id) ?? 9999) - (savedOrder.get(b.id) ?? 9999);
    });
  }, [dash]);

  const layouts = useMemo(() => {
    const savedLayout = Array.isArray(dash?.layout)
      ? dash.layout
          .filter((item) => orderedWidgets.some((widget) => widget.id === item.i))
          .map((item) => {
            const widget = orderedWidgets.find((w) => w.id === item.i);
            const isKpi = widget?.type === "kpi";

            return {
              i: item.i,
              x: Number(item.x || 0),
              y: Number(item.y || 0),
              w: isKpi ? 3 : Math.max(1, Math.min(12, Number(item.w || 4))),
              h: isKpi ? 3 : Math.max(2, Number(item.h || 4)),
              minW: isKpi ? 3 : Math.max(1, Number(item.minW || 2)),
              minH: isKpi ? 3 : Math.max(2, Number(item.minH || 2)),
            };
          })
      : [];

    const hasSavedLayout =
      savedLayout.length === orderedWidgets.length &&
      savedLayout.length > 0;

    const createFallbackLayout = () => {
      let currentY = 0;

      return orderedWidgets.map((widget) => {
        const isKpi = widget.type === "kpi";
        const isTable = widget.type === "table";

        const item = {
          i: widget.id,
          x: 0,
          y: currentY,
          w: isKpi ? 3 : 12,
          h: isKpi ? 3 : isTable ? 7 : 6,
          minW: isKpi ? 3 : 6,
          minH: isKpi ? 3 : 5,
        };

        currentY += item.h;
        return item;
      });
    };

    const desktopLayout = hasSavedLayout
      ? savedLayout
      : createFallbackLayout();

    const scaleLayout = (source, targetColumns) =>
      source.map((item) => {
        const scaledX = Math.floor((item.x / 12) * targetColumns);
        const scaledWidth = Math.max(
          1,
          Math.round((item.w / 12) * targetColumns)
        );

        return {
          ...item,
          x: Math.min(scaledX, Math.max(0, targetColumns - scaledWidth)),
          w: Math.min(targetColumns, scaledWidth),
          minW: Math.min(
            targetColumns,
            Math.max(1, Math.round((item.minW / 12) * targetColumns))
          ),
        };
      });

    const stackLayout = (source, columns) => {
      let currentY = 0;

      return source.map((item) => {
        const stacked = {
          ...item,
          x: 0,
          y: currentY,
          w: columns,
          minW: columns,
        };

        currentY += item.h;
        return stacked;
      });
    };

    return {
      lg: desktopLayout,
      md: scaleLayout(desktopLayout, 10),
      sm: stackLayout(desktopLayout, 6),
      xs: stackLayout(desktopLayout, 4),
    };
  }, [dash, orderedWidgets]);

  const buildDrillPayload = (sourceWidget, point = {}) => {
    const dim = sourceWidget.group_by || sourceWidget.dimension;
    const clickedLabel = point?.label ?? point?.name;

    const mergedFilters = [...dashboardFilters];

    if (
      dim &&
      clickedLabel !== undefined &&
      clickedLabel !== null &&
      clickedLabel !== "" &&
      clickedLabel !== "Unassigned" &&
      clickedLabel !== "Others"
    ) {
      mergedFilters.push({
        field: dim,
        op: "in",
        value: [clickedLabel],
      });
    }

    const tableWidget = {
      ...sourceWidget,
      type: "table",
      selected_columns: undefined,
      limit: 2000,
    };

    return {
      widget: tableWidget,
      filters: mergedFilters,
      clickedLabel,
      dimension: dim,
    };
  };

  const openDrillDown = async ({ widget, point }) => {
    if (!widget || widget.type === "table" || widget.type === "text") return;

    const payload = buildDrillPayload(widget, point || {});
    setDrill({
      title: widget.title || widget.type,
      widget,
      point: point || {},
      payload,
    });
    setDrillData(null);
    setDrillError("");
    setDrillSearch("");
    setDrillLoading(true);

    try {
      const r = await api.post("/widget/data", {
        widget: payload.widget,
        filters: payload.filters,
      }, { timeout: 35000 });

      setDrillData(r.data);
    } catch (e) {
      setDrillError(e?.response?.data?.detail || e.message || "Unable to load drill-down data.");
    } finally {
      setDrillLoading(false);
    }
  };

  const closeDrillDown = () => {
    setDrill(null);
    setDrillData(null);
    setDrillError("");
    setDrillSearch("");
  };

  const exportDrillCsv = async () => {
    if (!drill?.payload) return;

    try {
      const token = localStorage.getItem("camaroo_token");
      const resp = await fetch(`${API_BASE}/export/widget.csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          widget: drill.payload.widget,
          filters: drill.payload.filters,
        }),
      });

      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);

      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(drill.title || "drilldown").replace(/\s+/g, "_")}_details.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(e.message);
    }
  };

  const exportCsv = async (widget) => {
    try {
      const token = localStorage.getItem("camaroo_token");
      const resp = await fetch(`${API_BASE}/export/widget.csv`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ widget, filters: dashboardFilters }),
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${(widget.title || "export").replace(/\s+/g, "_")}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert(e.message); }
  };

  if (!dash) return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />
      <main className="erp-content">
        <div className="erp-card p-6 text-[12.5px]" data-testid="dashboard-loading-state">
          {loadError ? (
            <div className="max-w-md" style={{ color: "#991b1b" }}>
              <div className="mb-2 font-medium">Couldn&apos;t load this dashboard</div>
              <div className="mb-3" style={{ color: "var(--c-text-muted)" }}>{loadError}</div>
              <button className="btn btn-outline" onClick={fetchDashboard} data-testid="dashboard-retry">Retry</button>
            </div>
          ) : (
            <span style={{ color: "var(--c-text-muted)" }}>Loading dashboard…</span>
          )}
        </div>
      </main>
    </div>
  );

  const widgetById = Object.fromEntries((dash.widgets || []).map((w) => [w.id, w]));
  const isDemoDash = (dash.widgets || []).some((w) => (w.dataset_id || "").startsWith("demo:"));
  const isLiveDash = !isDemoDash && (dash.widgets || []).some((w) => w.dataset_id);
  const activeFilterChips = Object.entries(filterValues).flatMap(([field, val]) => {
    if (val == null || val === "" || val === "all") return [];
    if (typeof val === "object" && (val.from || val.to)) {
      return [{ field, text: `${field}: ${val.from || "…"} → ${val.to || "…"}` }];
    }
    const arr = Array.isArray(val) ? val : [val];
    return arr.filter(Boolean).map((v) => ({ field, text: `${field}: ${v}` }));
  });

  return (
    <div className="erp-home-shell">
      <ERPAnalyticsHeader />

      <main className="erp-content">
        <div className="erp-page-title-row">
          <div>
            <h1>{dash.name}</h1>
            {dash.description && <p>{dash.description}</p>}
          </div>

          <div className="flex items-center gap-2">
            {isLiveDash && <span className="erp-live-badge">Live QA Data</span>}

            <button className="btn btn-outline" onClick={doRefresh} disabled={refreshing} data-testid="dashboard-refresh">
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>

            {canAtLeast(user, "analyst") && (
              <button className="erp-primary-btn" onClick={() => nav(`/builder/${dash.id}`)} data-testid="dashboard-edit">
                <Pencil size={14} strokeWidth={1.7} /> Edit
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="card-flat px-4 py-3 flex flex-wrap items-end gap-3" data-testid="filter-bar">
            {(dash.filters || []).map((f) => (
              <FilterControl key={f.id} f={f} value={filterValues[f.field]} setValue={(v) => setFilter(f.field, v)} choices={dropdownChoices[f.field] || []} />
            ))}
            <div className="ml-auto flex gap-2">
              <button className="btn btn-outline" onClick={resetFilters} data-testid="filter-reset">Reset</button>
              <button className="btn btn-primary" onClick={doRefresh} data-testid="filter-apply">Apply</button>
            </div>
          </div>
          {activeFilterChips.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-[11.5px]" data-testid="active-filter-chips">
              <span style={{ color: "var(--c-text-muted)" }}>Active filters:</span>
              {activeFilterChips.map((c, i) => (
                <span key={i} className="pill" style={{ background: "#eef2ff", color: "#3730a3" }}>{c.text}</span>
              ))}
            </div>
          )}

          <div className="dash-canvas">
            <ResponsiveGrid
              className="layout"
              layouts={layouts}
              breakpoints={{ lg: 1200, md: 996, sm: 768 }}
              cols={{ lg: 12, md: 10, sm: 6 }}
              rowHeight={42}
              margin={[10, 10]}
              isDraggable={false}
              isResizable={false}
            >
              {orderedWidgets.map((w) => {
                if (!w) return null;
                return (
                  <div key={w.id} data-testid={`view-widget-${w.id}`}>
                    <div className="widget-shell">
                      <div className="wh">
                        <div>
                          <div className="wt">{w.title || w.type}</div>
                          {w.subtitle && <div className="ws">{w.subtitle}</div>}
                        </div>
                        <button className="btn btn-ghost" title="Export CSV" onClick={() => exportCsv(w)} data-testid={`export-${w.id}`}>
                          <Download size={13} strokeWidth={1.7} />
                        </button>
                      </div>
                      <div className="wb">
                        <WidgetRenderer
                          widget={w}
                          dashboardFilters={dashboardFilters}
                          onRefreshKey={refreshKey}
                          onDrillDown={openDrillDown}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </ResponsiveGrid>
          </div>
        </div>
      </main>

      {drill && (
        <DrillDownModal
          drill={drill}
          data={drillData}
          loading={drillLoading}
          error={drillError}
          search={drillSearch}
          setSearch={setDrillSearch}
          onClose={closeDrillDown}
          onExport={exportDrillCsv}
        />
      )}
    </div>
  );
}

function DrillDownModal({
  drill,
  data,
  loading,
  error,
  search,
  setSearch,
  onClose,
  onExport,
}) {
  const [zoom, setZoom] = useState(90);
  const tableWrapRef = useRef(null);

  const rawColumns = data?.columns || [];
  const rawRows = data?.rows || [];
  const sourceWidget = drill?.widget || {};
  const metricField = String(sourceWidget?.measure || "").toLowerCase();
  const metricValue = drill?.point?.value;
  const aggregation = sourceWidget?.aggregation || "sum";

  const priorityWords = [
    sourceWidget?.measure,
    "LineValueUSD",
    "LineValueNative",
    "MCS",
    "InvoiceGrossKGS",
    "AvailableMCS",
    "AvailableKGS",
    "OrderedMCS",
    "DispatchedMCS",
    "ReceivedQty",
    "OutMCS",
    "PriceNative",
    "Currency",
    "InvoiceDate",
    "InvoiceNumber",
    "PoNumber",
    "PoDate",
    "ClientName",
    "BuyerName",
    "ConsigneeName",
    "BrandName",
    "Brand",
    "Variety",
    "Grade",
    "PackingStyle",
    "ShipmentDate",
    "ContainerNo",
    "ColdStore",
    "ProductionDate",
  ].filter(Boolean);

  const isTechnicalColumn = (column) => {
    const name = String(column || "").toLowerCase();
    return (
      name === "id" ||
      name.endsWith("id") ||
      name.includes("invoiceid") ||
      name.includes("poid") ||
      name.includes("buyerid") ||
      name.includes("consigneeid") ||
      name.includes("processorid") ||
      name.includes("exporterid") ||
      name.includes("countryid") ||
      name.includes("clientid")
    );
  };

  const orderedIndexes = rawColumns
    .map((column, index) => ({ column, index }))
    .sort((a, b) => {
      const aMetric = metricField && String(a.column).toLowerCase() === metricField ? -10000 : 0;
      const bMetric = metricField && String(b.column).toLowerCase() === metricField ? -10000 : 0;

      const ap = priorityWords.findIndex((word) => String(a.column).toLowerCase() === String(word).toLowerCase());
      const bp = priorityWords.findIndex((word) => String(b.column).toLowerCase() === String(word).toLowerCase());

      const ar = ap >= 0 ? ap : isTechnicalColumn(a.column) ? 9999 : 5000;
      const br = bp >= 0 ? bp : isTechnicalColumn(b.column) ? 9999 : 5000;

      return (aMetric + ar) - (bMetric + br);
    });

  const columns = orderedIndexes.map((item) => item.column);
  const sourceRows = rawRows.map((row) => orderedIndexes.map((item) => row[item.index]));

  const visibleRows = sourceRows.filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return row.some((value) => String(value ?? "").toLowerCase().includes(q));
  });

  const metricColumnIndex = columns.findIndex(
    (column) => metricField && String(column).toLowerCase() === metricField
  );

  const hasKpiFooter =
    metricColumnIndex >= 0 &&
    metricValue !== null &&
    metricValue !== undefined &&
    metricValue !== "";

  const formatTotal = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const num = Number(String(value).replace(/,/g, ""));
    if (!Number.isFinite(num)) return String(value);
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
  };

  const scrollTable = (mode) => {
    const el = tableWrapRef.current;
    if (!el) return;

    if (mode === "start") el.scrollLeft = 0;
    if (mode === "left") el.scrollBy({ left: -600, behavior: "smooth" });
    if (mode === "right") el.scrollBy({ left: 600, behavior: "smooth" });
    if (mode === "end") el.scrollLeft = el.scrollWidth;
  };

  const clickedLabel = drill?.payload?.clickedLabel;
  const dimension = drill?.payload?.dimension;
  const metricLabel = sourceWidget?.measure || "Selected KPI";

  return (
    <div
      className="fixed inset-0 z-50 bg-white flex flex-col"
      data-testid="drilldown-fullscreen"
    >
      <div
        className="px-5 py-3 border-b flex items-start justify-between gap-3"
        style={{ borderColor: "var(--c-border)" }}
      >
        <div className="min-w-0">
          <div className="text-[16px] font-semibold truncate" style={{ color: "var(--c-text)" }}>
            {drill.title} — Full Detailed Data
          </div>
          <div className="text-[11.5px] mt-1" style={{ color: "var(--c-text-muted)" }}>
            {dimension && clickedLabel && clickedLabel !== "Unassigned" && clickedLabel !== "Others"
              ? `Filtered by ${dimension}: ${clickedLabel}`
              : clickedLabel === "Unassigned"
                ? "Unassigned selected. Showing source rows for review; blank/NULL mapping needs correction."
                : "Showing source rows behind this widget."}
          </div>
        </div>

        <button type="button" className="btn btn-ghost" onClick={onClose} title="Close">
          <X size={16} strokeWidth={1.8} />
        </button>
      </div>

      <div
        className="px-5 py-3 border-b grid grid-cols-12 gap-3 items-center"
        style={{ borderColor: "var(--c-border)" }}
      >
        <div className="col-span-12 lg:col-span-3 rounded-lg border px-3 py-2" style={{ borderColor: "var(--c-border)", background: "#f8fafc" }}>
          <div className="text-[10.5px] uppercase tracking-wide" style={{ color: "var(--c-text-muted)" }}>
            KPI Total
          </div>
          <div className="text-[18px] font-semibold numeric mt-0.5" style={{ color: "var(--c-text)" }}>
            {formatTotal(metricValue)}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--c-text-muted)" }}>
            Measure: {metricLabel}
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "var(--c-text-muted)" }}>
            Formula: {String(aggregation).toUpperCase()}({metricLabel})
          </div>
        </div>

        <div className="col-span-12 lg:col-span-5">
          <input
            className="input w-full"
            placeholder="Search detailed rows..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="col-span-12 lg:col-span-4 flex flex-wrap items-center justify-end gap-2">
          <button type="button" className="btn btn-outline" onClick={() => scrollTable("start")}>Start</button>
          <button type="button" className="btn btn-outline" onClick={() => scrollTable("left")}>← Left</button>
          <button type="button" className="btn btn-outline" onClick={() => scrollTable("right")}>Right →</button>
          <button type="button" className="btn btn-outline" onClick={() => scrollTable("end")}>End</button>
          <button type="button" className="btn btn-outline" onClick={onExport}>
            <Download size={13} strokeWidth={1.7} /> CSV
          </button>
        </div>

        <div className="col-span-12 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setZoom((z) => Math.max(65, z - 10))}
          >
            − Zoom
          </button>

          <span className="text-[12px] numeric" style={{ color: "var(--c-text-muted)" }}>
            {zoom}%
          </span>

          <button
            type="button"
            className="btn btn-outline"
            onClick={() => setZoom((z) => Math.min(130, z + 10))}
          >
            + Zoom
          </button>

          <div className="text-[11.5px]" style={{ color: "var(--c-text-muted)" }}>
            Showing {visibleRows.length.toLocaleString()} of {(sourceRows.length || 0).toLocaleString()} rows
            {data?.truncated ? " · preview rows loaded" : ""}
          </div>

          <div className="text-[11.5px] ml-auto" style={{ color: "var(--c-text-muted)" }}>
            Only the selected KPI measure is totaled. Technical ID columns are not totaled.
          </div>
        </div>
      </div>

      <div ref={tableWrapRef} className="flex-1 overflow-auto">
        {loading && (
          <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
            Loading detailed data…
          </div>
        )}

        {error && (
          <div className="p-5 text-[12.5px]" style={{ color: "#991b1b" }}>
            {error}
          </div>
        )}

        {!loading && !error && columns.length === 0 && (
          <div className="p-5 text-[12.5px]" style={{ color: "var(--c-text-muted)" }}>
            No detailed columns available.
          </div>
        )}

        {!loading && !error && columns.length > 0 && (
          <table
            className="tbl"
            style={{
              fontSize: `${Math.max(9, Math.min(13, zoom / 9))}px`,
              lineHeight: "1.2",
              minWidth: "max-content",
            }}
          >
            <thead className="sticky top-0 z-20 bg-white">
              <tr>
                {columns.map((column) => {
                  const isMetric = metricField && String(column).toLowerCase() === metricField;
                  return (
                    <th
                      key={column}
                      title={column}
                      style={{
                        whiteSpace: "nowrap",
                        padding: "8px 10px",
                        background: isMetric ? "#dbeafe" : "#fff",
                        color: isMetric ? "#1d4ed8" : undefined,
                        fontWeight: isMetric ? 700 : undefined,
                        position: isMetric ? "sticky" : undefined,
                        left: isMetric ? 0 : undefined,
                        zIndex: isMetric ? 30 : undefined,
                      }}
                    >
                      {column === "PriceNative" ? "Rate" : column}
                    </th>
                  );
                })}
              </tr>
            </thead>

            <tbody>
              {visibleRows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {row.map((value, columnIndex) => {
                    const isMetric = metricField && String(columns[columnIndex]).toLowerCase() === metricField;
                    return (
                      <td
                        key={columnIndex}
                        title={value === null || value === undefined ? "" : String(value)}
                        style={{
                          padding: "7px 10px",
                          whiteSpace: "normal",
                          maxWidth: 240,
                          verticalAlign: "top",
                          background: isMetric ? "#eff6ff" : undefined,
                          fontWeight: isMetric ? 700 : undefined,
                          position: isMetric ? "sticky" : undefined,
                          left: isMetric ? 0 : undefined,
                          zIndex: isMetric ? 20 : undefined,
                        }}
                      >
                        {value === null || value === undefined || value === "" ? "—" : formatReportValue(value, columns[columnIndex])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>

            {hasKpiFooter && (
              <tfoot className="sticky bottom-0 z-20 bg-white shadow-[0_-1px_0_var(--c-border)]">
                <tr>
                  {columns.map((column, columnIndex) => {
                    const isMetric = metricField && String(column).toLowerCase() === metricField;
                    return (
                      <td
                        key={column}
                        className="font-semibold numeric"
                        style={{
                          padding: "9px 10px",
                          borderTop: "1px solid var(--c-border)",
                          whiteSpace: "nowrap",
                          background: isMetric ? "#dbeafe" : "#fff",
                          color: isMetric ? "#1d4ed8" : undefined,
                        }}
                      >
                        {columnIndex === 0
                          ? "KPI TOTAL"
                          : isMetric
                            ? formatTotal(metricValue)
                            : ""}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        )}
      </div>
    </div>
  );
}


function FilterControl({ f, value, setValue, choices }) {
  if (f.type === "date_range") {
    const v = value || {};
    return (
      <div data-testid={`filter-${f.field}`}>
        <label className="label">{f.label}</label>
        <div className="flex gap-2">
          <DateInput className="input" style={{ width: 140 }} value={v.from || ""}
                 onChange={(e) => setValue({ ...v, from: e.target.value || undefined })} />
          <DateInput className="input" style={{ width: 140 }} value={v.to || ""}
                 onChange={(e) => setValue({ ...v, to: e.target.value || undefined })} />
        </div>
      </div>
    );
  }
  if (f.type === "dropdown") {
    return (
      <div data-testid={`filter-${f.field}`}>
        <label className="label">{f.label}</label>
        <select className="select" style={{ minWidth: 180 }} value={value || ""} onChange={(e) => setValue(e.target.value || "")}>
          <option value="">All</option>
          {(choices || []).map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
    );
  }
  return null;
}
